import { styled } from '@mui/material';

import { useState } from 'react';

import { useI18n } from '@app-velocity/i18n';
import { Typography } from '@app-velocity/ui';

import { useReportContext } from '../../../../context/useReportContext';
import { SinglePath, SinglePathRun } from '../../../../types';
import { PathRunScreens } from '../../../PathRunScreens';
import { PathRunStepDetails } from '../../../PathRunStepDetails';

const StyledPathRunPreview = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

const StyledHeader = styled('header')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(3),
  flexWrap: 'wrap',
}));

const StyledTitle = styled(Typography)({
  fontSize: 14,
  fontWeight: 700,
});

const StyledExpandedSection = styled('div')(({ theme }) => ({
  padding: theme.spacing(4),
  border: '1px solid #e3e3e3',
  borderRadius: theme.spacing(4),
  // -1 top margin to allow overlap with bottom whit border of the selected slide
  marginTop: -1,
}));

export type PathRunPreviewProps = {
  path: SinglePath;
  run: SinglePathRun;
  index: number;
};

export const PathRunPreview = ({ path, run, index }: PathRunPreviewProps) => {
  const { t } = useI18n();
  const { type } = useReportContext();
  const [expandedViewIndex, setExpandedViewIndex] = useState<number | null>(
    null
  );

  const hasDetails =
    type === 'vibe' ||
    (!!run.reviewId && !run.reviewId.includes('simulation_'));

  const handleViewClick = (viewIndex: number) => {
    setExpandedViewIndex((current) =>
      current === viewIndex ? null : viewIndex
    );
  };

  const handlePreviousView = () => {
    if (expandedViewIndex === null) return;

    setExpandedViewIndex((current) => {
      if (current === null) return current;

      return current > 0 ? current - 1 : run.views.length - 1;
    });
  };

  const handleNextView = () => {
    if (expandedViewIndex === null) return;

    setExpandedViewIndex((current) => {
      if (current === null) return current;

      return current < run.views.length - 1 ? current + 1 : 0;
    });
  };

  const expandedView =
    expandedViewIndex !== null ? run.views[expandedViewIndex] : null;

  return (
    <StyledPathRunPreview>
      <StyledHeader>
        <StyledTitle component="h4">
          {t('prototypes:report.paths.breakdown.run', { number: index + 1 })}
        </StyledTitle>
      </StyledHeader>
      <PathRunScreens
        path={path}
        views={run.views}
        hasDetails={hasDetails}
        selectedViewIndex={expandedViewIndex}
        onViewClick={handleViewClick}
      />
      {expandedView ? (
        <StyledExpandedSection>
          <PathRunStepDetails
            reviewId={run.reviewId}
            stepNumber={expandedViewIndex!}
            view={expandedView}
            persona={path.persona || 'default'}
            personaConfig={path.personaConfig}
            onPrevious={handlePreviousView}
            onNext={handleNextView}
          />
        </StyledExpandedSection>
      ) : null}
    </StyledPathRunPreview>
  );
};
